/**
 * Компоненты для отображения отзывов в чате:
 * - ReviewButtons: компактные кнопки в сообщении
 * - ReviewPopup: полноэкранный попап с деталями отзыва
 */

import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Star, ChevronRight, X } from 'lucide-react';
import { fetchReviewsByKitCode, KitReview, ReviewSection } from '@/services/api/reviews.api';
import { useConfigStore } from '@/store/configStore';
import { useScrollLock } from '@/hooks/useScrollLock';
import { getWidgetPortalHost } from '@/utils/widgetHost';
import styles from './ReviewButtons.module.css';

function getLocalizedTitle(review: KitReview, lang: string): string {
  if (lang === 'en' && review.title_en) return review.title_en;
  if (lang === 'de' && review.title_de) return review.title_de;
  return review.title;
}

function getLocalizedSections(review: KitReview, lang: string): ReviewSection[] {
  if (lang === 'en' && review.sections_en?.length) return review.sections_en;
  if (lang === 'de' && review.sections_de?.length) return review.sections_de;
  return review.sections;
}

interface ReviewButtonsProps {
  kitCode: string;
}

export function ReviewButtons({ kitCode }: ReviewButtonsProps) {
  const [reviews, setReviews] = useState<KitReview[]>([]);
  const [selectedReview, setSelectedReview] = useState<KitReview | null>(null);
  const [loading, setLoading] = useState(true);
  const language = useConfigStore((s) => s.language);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchReviewsByKitCode(kitCode)
      .then((data) => {
        if (!cancelled) {
          setReviews(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [kitCode]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.skeleton} />
        <div className={styles.skeleton} />
      </div>
    );
  }

  if (reviews.length === 0) return null;

  return (
    <>
      <div className={styles.container}>
        {reviews.map((review) => (
          <button
            key={review.id}
            type="button"
            className={styles.button}
            onClick={() => setSelectedReview(review)}
          >
            <span className={styles.icon}>
              <Star size={14} strokeWidth={2} />
            </span>
            <span className={styles.text}>{getLocalizedTitle(review, language)}</span>
            <span className={styles.arrow}>
              <ChevronRight size={14} strokeWidth={2} />
            </span>
          </button>
        ))}
      </div>

      {selectedReview &&
        createPortal(
          <ReviewPopup
            review={selectedReview}
            language={language}
            onClose={() => setSelectedReview(null)}
          />,
          getWidgetPortalHost(),
        )}
    </>
  );
}

interface ReviewPopupProps {
  review: KitReview;
  language: string;
  onClose: () => void;
}

function ReviewPopup({ review, language, onClose }: ReviewPopupProps) {
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (lightboxUrl) setLightboxUrl(null);
        else onClose();
      }
    },
    [onClose, lightboxUrl],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useScrollLock(true);

  const title = getLocalizedTitle(review, language);
  const sections = getLocalizedSections(review, language);

  const photosClass = review.images.length === 1
    ? `${styles.photos} ${styles.photoSingle}`
    : styles.photos;

  return (
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true">
      <div className={styles.popup} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={onClose} type="button" aria-label="Close">
          <X size={18} strokeWidth={2} />
        </button>

        {review.images.length > 0 && (
          <div className={photosClass}>
            {review.images.map((url, i) => (
              <img
                key={i}
                src={url}
                alt={`${title} ${i + 1}`}
                onClick={() => setLightboxUrl(url)}
              />
            ))}
          </div>
        )}

        <div className={styles.popupContent}>
          <h3 className={styles.popupTitle}>{title}</h3>
          {sections.map((section, i) => (
            <div key={i} className={styles.section}>
              {section.subtitle && (
                <p className={styles.sectionSubtitle}>{section.subtitle}</p>
              )}
              <p className={styles.sectionText}>{section.text}</p>
            </div>
          ))}
        </div>
      </div>

      {lightboxUrl && (
        <div
          className={styles.overlay}
          onClick={(e) => { e.stopPropagation(); setLightboxUrl(null); }}
          style={{ zIndex: 100000 }}
        >
          <img
            src={lightboxUrl}
            alt=""
            style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: '8px' }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
